import { LiveTv } from '@mui/icons-material';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useState, type SyntheticEvent } from 'react';
import type { RemoteChannel } from '~/api/remote';

interface ChannelsProps {
  channels: readonly RemoteChannel[];
  onTune: (channelId: number) => void;
}

// A touch device leaves :hover applied after a tap, so a tapped tile would stay shaded until
// something else is tapped. Same gate the remote keys use.
const HOVER = '@media (hover: hover)';

// Tiles are sized by how many fit rather than by a breakpoint, so the same grid fills a phone
// held either way: two or three across a portrait phone, five across a landscape one, without
// either orientation needing its own rule. A grid rather than wrapping flex, because grid
// tracks are identical on every row by construction; flex would have to let tiles grow to
// fill a row, which leaves a part-filled last row wider than the rows above it.
const gridSx = {
  display: 'grid',
  gap: 1.5,
  gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))',
  width: '100%',
} as const;

const tileSx = {
  alignItems: 'stretch',
  bgcolor: 'common.white',
  border: 1,
  borderColor: 'borders.main',
  borderRadius: 2,
  flexDirection: 'column',
  overflow: 'hidden',
  transition: 'background-color 120ms, transform 120ms',
  '&.Mui-focusVisible': { bgcolor: 'grey.100' },
  [HOVER]: { '&:hover': { bgcolor: 'grey.100' } },
  '&:active': { transform: 'scale(0.97)' },
} as const;

// Logos run from taller-than-wide crests to 4:1 wordmarks, so matching their widths leaves the
// square ones small and matching their heights leaves the wide ones as strips. Equal *area* is
// what reads as the same size, and it needs each image's own proportions, which only arrive
// with its load event.
//
// For a logo of aspect `r` drawn in a box of aspect BOX_RATIO, covering LOGO_AREA of that box
// means a width of sqrt(LOGO_AREA * r / BOX_RATIO), as a share of the box's width.
const LOGO_AREA = 0.32;
const BOX_RATIO = 16 / 9;

const logoWidth = (ratio: number) => `${Math.sqrt((LOGO_AREA * ratio) / BOX_RATIO) * 100}%`;

// A channel always has a thumbnail record, but the object behind it can be gone or unreadable.
// A fan gets the glyph rather than a torn-image icon, matching how ChannelCard handles a channel
// with nothing to show.
const ChannelLogo = ({ channel }: { channel: RemoteChannel }) => {
  // Remembers which url failed: urls are presigned and can expire under an open page, and the
  // re-signed one the page refetches deserves its own attempt.
  const [failedUri, setFailedUri] = useState<string | null>(null);
  const [ratio, setRatio] = useState<number | null>(null);
  const failed = failedUri === channel.thumbnailUri;

  return (
    <Box
      sx={{
        alignItems: 'center',
        aspectRatio: '16 / 9',
        // eslint-disable-next-line no-restricted-syntax -- fallback for a user-picked channel color
        bgcolor: channel.backgroundColor || '#f8f8f8',
        color: 'grey.500',
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
      }}
    >
      {failed ? (
        <LiveTv sx={{ fontSize: 32 }} />
      ) : (
        <Box
          alt=""
          component="img"
          onError={() => setFailedUri(channel.thumbnailUri)}
          onLoad={(event: SyntheticEvent<HTMLImageElement>) =>
            setRatio(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight)
          }
          src={channel.thumbnailUri}
          sx={{
            // Logos are drawn for whatever background the channel carries, so they are
            // contained rather than cropped: filling the tile would clip wordmarks.
            objectFit: 'contain',
            width: ratio == null ? 'auto' : logoWidth(ratio),
            height: 'auto',
            // The caps stop an extreme wordmark or a tall crest from reaching the tile's edges.
            // Either one then fits rather than matching area, which is the right trade: a logo
            // touching the border looks broken, a slightly small one does not.
            maxHeight: '85%',
            maxWidth: '90%',
            // Out of flow, and centred by the inset/auto-margin pair. In flow, a portrait logo
            // (they range from 15:1 to taller-than-wide) stretches the box past its aspect
            // ratio, because a percentage max-height has nothing to resolve against when the
            // height comes from `aspect-ratio`, leaving every tile in a row a different shape.
            inset: 0,
            margin: 'auto',
            position: 'absolute',
          }}
        />
      )}
    </Box>
  );
};

// Only rendered with something to show: the page hides the tab bar entirely when the guide is
// empty, so there is no empty state to fall into here.
export const Channels = ({ channels, onTune }: ChannelsProps) => (
  <Box sx={gridSx}>
    {channels.map((channel) => (
      <ButtonBase key={channel.id} onClick={() => onTune(channel.id)} sx={tileSx}>
        <ChannelLogo channel={channel} />

        <Box
          sx={{
            alignItems: 'baseline',
            display: 'flex',
            gap: 0.75,
            minWidth: 0,
            px: 1,
            py: 0.75,
            width: '100%',
          }}
        >
          <Typography color="text.secondary" fontWeight={700} variant="caption">
            {channel.number}
          </Typography>
          {/* One line: a tile's width is set by the grid, and a wrapping name would make the
                rows around it taller for no gain. */}
          <Typography noWrap textAlign="left" variant="body2">
            {channel.name}
          </Typography>
        </Box>
      </ButtonBase>
    ))}
  </Box>
);
